'use client';

import React, { useState, useEffect } from 'react';
import { Collapse, Container, Grid, Typography, Link as MuiLink, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Link from 'next/link';
import useComponentVisible from '../../../hooks/useComponentVisible';
import { useStyles } from '../style';
import FeaturedImage from './FeaturedImage';

/**
 * @name DropdownMenu
 * @description Displays list of primary navs
 * @param {object} root0  - destructured object
 * @param {string} root0.handleClose - Function to collpse drop down list
 * @param {string} root0.nav - Data from Strapi
 * @returns {JSX.Element}  ReactJs Element
 */
const DropdownMenu = ({ handleClose, nav }) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [expand, setExpand] = useState(false);
  const [, setData] = useState(null);
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(true);

  useEffect(() => {
    if (nav) {
      setExpand(Boolean(nav));
      setData(nav);
      setIsComponentVisible(Boolean(nav));
    } else {
      setExpand(false);
      setData(null);
    }
  }, [nav, setIsComponentVisible]);

  useEffect(() => {
    if (!isComponentVisible) {
      handleClose();
    }
  }, [isComponentVisible]);

  return (
    <Container maxWidth='auto' ref={ref} sx={styles.mainNavStyle.dropdownContainer}>
      <Container maxWidth='lg'>
        {isComponentVisible && (
          <Collapse
            in={expand}
            onClose={handleClose}
            timeout='auto'
            unmountOnExit
            sx={{ width: '100%' }}
          >
            <Grid
              container
              direction='row'
              alignItems='flex-start'
              sx={styles.mainNavStyle.gridContainer}
            >
              <FeaturedImage nav={nav} />
              <Grid item container sm={8} direction='row' justifyContent='flex-end'>
                {nav?.PrimaryNavL2?.map((link, linkIndex) => (
                  <Grid item sm={6} spacing={4} sx={{ p: 2 }} key={linkIndex}>
                    <MuiLink
                      href=''
                      sx={{
                        pointerEvents: 'none',
                        textDecoration: 'none',
                        color: 'inherit',
                      }}
                      variant='h5'
                      onClick={e => {
                        e.preventDefault();
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                        }
                      }}
                    >
                      {link?.title}
                    </MuiLink>
                    {link?.PrimaryNavL3?.map((data, l3Index) =>
                      data?.Link?.map((menuLink, i) => {
                        const isLast =
                          linkIndex === nav.PrimaryNavL2.length - 1 &&
                          l3Index === link.PrimaryNavL3.length - 1 &&
                          i === data.Link.length - 1;
                        return (
                          <Box key={i} sx={{ display: 'block' }}>
                            <Link
                              onClick={handleClose}
                              href={menuLink?.link}
                              passHref
                              underline='hover'
                              legacyBehavior
                            >
                              <Typography
                                variant='body1'
                                component='a'
                                aria-label={menuLink?.ariaLabel}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleClose();
                                    e.currentTarget.click();
                                  }
                                  if (e.key === 'Tab' && !e.shiftKey && isLast) {
                                    handleClose();
                                  }
                                }}
                                sx={{
                                  ...styles.mainNavStyle.navLinks,
                                  display: 'inline-block',
                                  margin: '0.5rem 0',
                                  padding: 0,
                                }}
                              >
                                {menuLink?.title}
                              </Typography>
                            </Link>
                          </Box>
                        );
                      }))}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Collapse>
        )}
      </Container>
    </Container>
  );
};

DropdownMenu.displayName = 'DropdownMenu';
export default DropdownMenu;
